import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, BlockStack, TextField, Banner, Text } from '@shopify/polaris';
import { ordersService } from '../../services/ordersService';
import StoryEntryCard from '../orders/create/StoryEntryCard';
import { useOrderQuote } from '../../hooks/useOrderQuote';
import {
  buildOrderItems,
  buildQuoteRequest,
  fingerprintQuoteRequest
} from '../../utils/storefrontOrder/payload';
import { validateEntry } from '../../utils/storefrontOrder/validation';
import { getOverrideErrors, isPaidOnline } from '../../utils/orderEditRules';
import { isAddonLine } from '../../utils/orderAddons';

const makeEntry = () => ({
  key: 'edit-entry',
  story: null,
  variant: null,
  languageCode: 'en',
  kidName: '',
  photos: []
});

const makeRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `edit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const lineAmount = line => Number(line?.price || 0) * Number(line?.quantity ?? 1);

const money = value => Number(value || 0).toFixed(2);

const getRemovedAmount = (order, lineItem) => {
  if (!lineItem) return 0;
  const addons = (order?.line_items || []).filter(item =>
    item !== lineItem
    && isAddonLine(item)
    && (item.properties || []).some(
      prop => prop?.name === '_Related Story' && prop?.value === lineItem.name
    )
  );
  return addons.reduce((sum, item) => sum + lineAmount(item), lineAmount(lineItem));
};

const OrderItemEditModal = ({ isOpen, mode, order, lineItem, catalog, onClose, onSubmitted }) => {
  const isReplace = mode === 'replace';
  const [entry, setEntry] = useState(makeEntry);
  const [overridePrice, setOverridePrice] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [entryDirty, setEntryDirty] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  const submittingRef = useRef(false);
  const requestIdRef = useRef(null);

  // Keyed on ids, not object identity, so a parent refresh does not wipe the form.
  const orderId = order?.order_id;
  const lineItemId = lineItem?.id;
  useEffect(() => {
    if (isOpen) {
      setEntry(makeEntry());
      setOverridePrice('');
      setOverrideReason('');
      setSubmitError('');
      setAttempted(false);
      setEntryDirty(false);
      setPriceTouched(false);
      setReasonTouched(false);
      setSubmitting(false);
      submittingRef.current = false;
      requestIdRef.current = makeRequestId();
    }
  }, [isOpen, mode, orderId, lineItemId]);

  const quoteRequest = useMemo(() => {
    if (!isOpen || !entry.story || !entry.variant) return null;
    return buildQuoteRequest({ entries: [entry], shippingValue: 'regular' });
  }, [isOpen, entry]);
  const {
    quote: rawQuote,
    quoteFingerprint,
    status: quoteStatus,
    error: quoteError
  } = useOrderQuote(quoteRequest);

  const currentFingerprint = quoteRequest ? fingerprintQuoteRequest(quoteRequest) : null;
  const quote = rawQuote
    && currentFingerprint
    && quoteFingerprint === currentFingerprint
    && quoteStatus !== 'error'
    ? rawQuote
    : null;

  const quoteLines = quote?.line_items || [];
  const catalogPrice = quoteLines.length ? Number(quoteLines[0].original_total) : null;
  const overrideErrors = getOverrideErrors(overridePrice, overrideReason);
  const overrideError = overrideErrors.price || overrideErrors.reason || null;
  const hasOverride = String(overridePrice).trim() !== '';
  const priceFieldError = (priceTouched || hasOverride || attempted) ? overrideErrors.price : undefined;
  const reasonFieldError = (reasonTouched || String(overrideReason).trim() !== '' || attempted)
    ? overrideErrors.reason
    : undefined;

  const newTotal = useMemo(() => {
    if (!quoteLines.length) return null;
    const rest = quoteLines.slice(1).reduce((s, l) => s + Number(l.original_total || 0), 0);
    const story = hasOverride && !overrideError
      ? Number(overridePrice)
      : Number(quoteLines[0].original_total || 0);
    return story + rest;
  }, [quoteLines, hasOverride, overrideError, overridePrice]);

  const removed = isReplace ? getRemovedAmount(order, lineItem) : 0;
  const delta = newTotal === null ? null : newTotal - removed;
  const paidReplace = isReplace && isPaidOnline(order);
  const lowersPaid = paidReplace && delta !== null && delta < 0;
  // Fail closed: a paid-online replace needs a current, loaded quote to verify.
  const priceUnverified = paidReplace && delta === null;
  let priceHint = '';
  if (priceUnverified) {
    priceHint = quoteStatus === 'error'
      ? 'Price unavailable, cannot verify this edit'
      : 'Waiting for the price…';
  }

  const entryErrors = validateEntry(entry);
  const canSubmit = entryErrors.length === 0 && !overrideError && !lowersPaid
    && !priceUnverified && !submitting;

  const handleClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  const handleEntryChange = next => {
    setEntryDirty(true);
    setEntry(next);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    setAttempted(true);
    if (!canSubmit) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    const body = {
      items: buildOrderItems([entry]),
      edit_request_id: requestIdRef.current
    };
    if (hasOverride) {
      body.price_override = { unit_price: Number(overridePrice), reason: overrideReason.trim() };
    }
    try {
      const result = isReplace
        ? await ordersService.replaceOrderStory(order.order_id, lineItem.id, body)
        : await ordersService.addStoryToOrder(order.order_id, body);
      if (onSubmitted) onSubmitted(result);
      onClose();
    } catch (error) {
      const details = error?.details;
      const detailText = details
        ? (typeof details === 'string' ? details : JSON.stringify(details))
        : '';
      setSubmitError([error?.message || 'Failed to update order', detailText].filter(Boolean).join(': '));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={isReplace ? 'Replace story' : 'Add story'}
      primaryAction={{
        content: isReplace ? 'Replace story' : 'Add story',
        onAction: handleSubmit,
        loading: submitting,
        disabled: !canSubmit
      }}
      secondaryActions={[{ content: 'Cancel', onAction: handleClose, disabled: submitting }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {submitError && <Banner tone="critical" title="Could not update order">{submitError}</Banner>}
          <StoryEntryCard
            entry={entry}
            index={0}
            catalog={catalog}
            onChange={handleEntryChange}
            showErrors={attempted || entryDirty}
          />
          <BlockStack gap="200">
            <Text as="p">
              Catalog price:{' '}
              {catalogPrice !== null
                ? money(catalogPrice)
                : quoteStatus === 'loading' ? 'Calculating...' : 'Select a story'}
            </Text>
            {quoteError && <Text as="p" tone="critical">{quoteError}</Text>}
            <TextField
              label="Override unit price (optional)"
              value={overridePrice}
              onChange={setOverridePrice}
              onBlur={() => setPriceTouched(true)}
              inputMode="decimal"
              autoComplete="off"
              error={priceFieldError}
            />
            <TextField
              label="Override reason"
              value={overrideReason}
              onChange={setOverrideReason}
              onBlur={() => setReasonTouched(true)}
              autoComplete="off"
              helpText="Required (at least 10 characters) when overriding the price"
              error={reasonFieldError}
            />
            {priceHint && <Text as="p" tone="subdued">{priceHint}</Text>}
            {delta !== null && (
              <Text as="p">
                Order total will change by {delta >= 0 ? '+' : '-'}{money(Math.abs(delta))}
              </Text>
            )}
            {lowersPaid && (
              <Banner tone="warning">This would lower the total of an already-paid order</Banner>
            )}
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default OrderItemEditModal;
